// Feature 009: Deal Operating System
// Main DealDesk pipeline view component with drag-and-drop Kanban
// Design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  DndContext,
  DragOverlay,
  rectIntersection,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import StatusColumn from './StatusColumn';
import OpportunityCard from './OpportunityCard';
import NewOpportunityModal from './NewOpportunityModal';
import DocumentDropZone from './DocumentDropZone';
import WelcomeBanner from './WelcomeBanner';
import { createDealDeskSubscription } from '../../channels/deal_desk_channel';
import useKeyboardShortcuts from '../../hooks/useKeyboardShortcuts';
import { useAssistant } from '../../contexts/AssistantContext';
import { useToast } from '../shared/Toast';

// Full licensing lifecycle stages per USOKIE Platform spec
const STATUSES = [
  { key: 'new', label: 'Intake', code: 'STS.01', phase: 'intake' },
  { key: 'qualified', label: 'Terms Aligned', code: 'STS.02', phase: 'intake' },
  { key: 'proposal', label: 'Proposal Draft', code: 'STS.03', phase: 'contract' },
  { key: 'negotiation', label: 'Contract Review', code: 'STS.04', phase: 'contract' },
  { key: 'signed', label: 'Contract Signed', code: 'STS.05', phase: 'contract' },
  { key: 'design_submitted', label: 'Design Submitted', code: 'STS.06', phase: 'design' },
  { key: 'design_approved', label: 'Design Approved', code: 'STS.07', phase: 'design' },
  { key: 'sku_defined', label: 'SKU Defined', code: 'STS.08', phase: 'production' },
  { key: 'production_planning', label: 'Production Planning', code: 'STS.09', phase: 'production' },
  { key: 'in_production', label: 'In Production', code: 'STS.10', phase: 'production' },
  { key: 'in_transit', label: 'In Transit', code: 'STS.11', phase: 'production' },
  { key: 'in_market', label: 'In Market', code: 'STS.12', phase: 'market' },
  { key: 'reporting', label: 'Reporting', code: 'STS.13', phase: 'market' },
  { key: 'closed', label: 'Closed', code: 'STS.14', phase: 'closed' },
];

const DealDeskApp = ({ csrfToken, currentUser }) => {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [connected, setConnected] = useState(false);
  const [showDropZone, setShowDropZone] = useState(false);
  const subscriptionRef = useRef(null);

  // Use the persistent assistant from context
  const { openAssistant } = useAssistant();

  // Toast notifications for soft validation
  const { showToast } = useToast();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Require 5px movement before activating drag - allows clicks to work
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(TouchSensor, {
      // Touch requires 10px movement and 250ms delay to avoid scroll interference
      activationConstraint: {
        delay: 250,
        tolerance: 10,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Custom collision detection that prioritizes columns for Kanban-style drop
  const customCollisionDetection = useCallback((args) => {
    // Use rectIntersection to find ALL containers the pointer overlaps with
    // This will include both the column AND any cards within it
    const rectCollisions = rectIntersection(args);

    // Find column collisions (status keys like 'new', 'qualified', etc.)
    const columnCollisions = rectCollisions.filter(
      collision => STATUSES.some(s => s.key === collision.id)
    );

    // ALWAYS prefer the column - this ensures dragging over cards
    // still registers as being over the column for highlighting
    if (columnCollisions.length > 0) {
      return columnCollisions;
    }

    // If no column found, check pointer-based detection as fallback
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      // Try to find a column from pointer collisions too
      const pointerColumnCollisions = pointerCollisions.filter(
        collision => STATUSES.some(s => s.key === collision.id)
      );
      if (pointerColumnCollisions.length > 0) {
        return pointerColumnCollisions;
      }
      return pointerCollisions;
    }

    return rectCollisions;
  }, []);

  const fetchOpportunities = useCallback(async () => {
    try {
      const response = await fetch('/api/v1/opportunities/pipeline', {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch opportunities');
      }

      const data = await response.json();
      setOpportunities(data.opportunities || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [csrfToken]);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  // Re-fetch when navigating back with browser button or when page becomes visible
  useEffect(() => {
    // Handle browser back/forward navigation (bfcache restoration)
    const handlePageShow = (event) => {
      if (event.persisted) {
        // Page was restored from bfcache - re-fetch to get fresh data
        fetchOpportunities();
      }
    };

    // Handle tab visibility change (optional but good UX)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchOpportunities();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchOpportunities]);

  // Action Cable subscription for real-time updates
  useEffect(() => {
    subscriptionRef.current = createDealDeskSubscription({
      onConnected: () => setConnected(true),
      onDisconnected: () => setConnected(false),
      onStatusChange: (opportunity) => {
        setOpportunities(prev =>
          prev.map(o => o.id === opportunity.id ? { ...o, ...opportunity } : o)
        );
      },
      onOpportunityCreated: (opportunity) => {
        setOpportunities(prev => {
          // Avoid duplicates
          if (prev.some(o => o.id === opportunity.id)) return prev;
          return [...prev, opportunity];
        });
      },
      onOpportunityDeleted: (opportunityId) => {
        setOpportunities(prev => prev.filter(o => o.id !== opportunityId));
      }
    });

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
      }
    };
  }, []);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    'Escape': () => {
      setShowNewModal(false);
      setShowDropZone(false);
    },
    'n': () => setShowNewModal(true),
    'r': () => fetchOpportunities(),
    'a': () => openAssistant(), // Open AI assistant
  }, true); // Always allow shortcuts

  // Handle file drop from DocumentDropZone - opens assistant
  const handleFileDrop = useCallback((files) => {
    setShowDropZone(false);
    openAssistant();
    // The PersistentAssistant component handles file upload internally
  }, [openAssistant]);

  // Handle opportunity creation from AI assistant
  const handleAssistantCreate = async (opportunityData) => {
    await handleCreateOpportunity(opportunityData);
  };

  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over) return;

    const opportunityId = active.id;

    // Determine target status - over.id could be a status key OR an opportunity ID
    const validStatuses = STATUSES.map(s => s.key);
    let newStatus;

    if (validStatuses.includes(over.id)) {
      // Dropped on a column directly
      newStatus = over.id;
    } else {
      // Dropped on another opportunity card - use that card's status
      const targetOpportunity = opportunities.find(o => o.id.toString() === over.id);
      if (!targetOpportunity) return;
      newStatus = targetOpportunity.status;
    }

    // Find the opportunity being dragged
    const opportunity = opportunities.find(o => o.id === parseInt(opportunityId));
    if (!opportunity || opportunity.status === newStatus) return;

    // Optimistically update UI
    setOpportunities(prev =>
      prev.map(o =>
        o.id === parseInt(opportunityId) ? { ...o, status: newStatus } : o
      )
    );

    // Find the stage label for the new status
    const stageLabel = STATUSES.find(s => s.key === newStatus)?.label || newStatus;

    // Make API call to update status
    try {
      const response = await fetch(`/api/v1/opportunities/${opportunityId}/transition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });

      if (!response.ok) {
        // Revert on failure
        fetchOpportunities();
        throw new Error('Failed to update status');
      }

      // Check for warnings in the response
      const data = await response.json();
      if (data.warnings?.length > 0) {
        showToast({
          type: 'warning',
          message: `Moved to ${stageLabel}. Note: ${data.warnings.join(', ')}`,
          duration: 5000
        });
      } else {
        showToast({
          type: 'success',
          message: `Moved to ${stageLabel}`,
          duration: 3000
        });
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateOpportunity = async (opportunityData) => {
    try {
      // Extract entities from opportunityData (they're separate params in the API)
      const { counterparty, ip_asset, ...coreOpportunityData } = opportunityData;

      const requestBody = {
        opportunity: coreOpportunityData,
      };

      // Add counterparty as separate param if provided
      if (counterparty) {
        requestBody.counterparty = counterparty;
      }

      // Add ip_asset as separate param if provided
      if (ip_asset) {
        requestBody.ip_asset = ip_asset;
      }

      const response = await fetch('/api/v1/opportunities', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        credentials: 'include',
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error('Failed to create opportunity');
      }

      // Don't add to local state - Action Cable will broadcast the new opportunity
      // This prevents duplicate entries from race conditions
      await response.json();
      setShowNewModal(false);
    } catch (err) {
      setError(err.message);
    }
  };

  const getOpportunitiesByStatus = (status) => {
    return opportunities.filter(o => o.status === status);
  };

  const activeOpportunity = activeId
    ? opportunities.find(o => o.id === parseInt(activeId))
    : null;

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border border-[rgba(255,251,247,0.2)] border-t-[#a1c6a2] animate-spin mx-auto" />
          <p
            className="mt-6 text-[10px] text-[rgba(255,251,247,0.5)] tracking-[0.2em] uppercase"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            Loading Pipeline
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
        {/* Header */}
        <header className="border-b border-[rgba(255,251,247,0.1)] bg-[#0a0a0a] px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <span
                className="text-[10px] text-[rgba(255,251,247,0.4)] tracking-[0.2em] uppercase block mb-1"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                Pipeline Management
              </span>
              <h1 className="text-3xl font-bold text-[#fffbf7]">
                Deal Desk
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {/* AI Assistant button */}
              <button
                onClick={openAssistant}
                className="inline-flex items-center px-4 py-2 border border-[rgba(255,251,247,0.3)] text-[rgba(255,251,247,0.7)] text-xs font-semibold tracking-[0.15em] uppercase hover:border-[#a1c6a2] hover:text-[#a1c6a2] transition-colors"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                AI Assistant
              </button>
              <button
                onClick={() => setShowNewModal(true)}
                className="inline-flex items-center px-4 py-2 border border-[#fffbf7] text-[#fffbf7] text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#fffbf7] hover:text-black transition-colors"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Opportunity
              </button>
            </div>
          </div>
        </header>

        {/* Welcome Banner */}
        <WelcomeBanner />

        {/* Error Banner */}
        {error && (
          <div className="mx-8 mt-4 p-4 border border-[#c47474] bg-[rgba(196,116,116,0.1)]">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-[#c47474]" />
              <span
                className="text-[10px] text-[#c47474] tracking-[0.15em] uppercase"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                Error
              </span>
              <p className="text-sm text-[#c47474]">{error}</p>
            </div>
          </div>
        )}

        {/* Kanban Board */}
        <div className="flex-1 overflow-x-scroll p-8 scrollbar-sage">
          <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <div className="flex gap-4 min-w-max h-full">
              {STATUSES.map(status => (
                <StatusColumn
                  key={status.key}
                  status={status}
                  opportunities={getOpportunitiesByStatus(status.key)}
                />
              ))}
            </div>

            <DragOverlay>
              {activeOpportunity ? (
                <OpportunityCard opportunity={activeOpportunity} isDragging />
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>

        {/* New Opportunity Modal */}
        {showNewModal && (
          <NewOpportunityModal
            onClose={() => setShowNewModal(false)}
            onSubmit={handleCreateOpportunity}
            csrfToken={csrfToken}
          />
        )}

        {/* Document Drop Zone Overlay */}
        <DocumentDropZone
          isActive={showDropZone}
          onDragStateChange={setShowDropZone}
          onFileDrop={handleFileDrop}
        />

    </>
  );
};

export default DealDeskApp;
